import { useState } from 'react';
import { Search, Share2 } from 'lucide-react';
import type {
  AgentComponent,
  ComponentCallDirection,
  ComponentCategory,
  ComponentChannel,
  DecisionPreset,
  WorkflowStep,
} from '@vagusx/clients';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { unavailableReason } from './graph';

/**
 * Every kind of step VagusX can run, from the component catalog — never a
 * list kept here — laid out as a grid by group. A step this workflow cannot
 * use (a phone-only step on a text workflow, an inbound-only step on an
 * outbound call) is shown, not hidden, with the reason on the card, so an
 * author looking for it learns why rather than concluding it does not exist.
 *
 * Below the kinds are the practice's SHARED steps: configured steps another
 * workflow already uses, words and all.
 */

const CATEGORY_ORDER: readonly ComponentCategory[] = [
  'open',
  'identity',
  'collect',
  'schedule',
  'inform',
  'handoff',
  'decide',
  'close',
];

const CATEGORY_TITLES: Record<ComponentCategory, string> = {
  open: 'Open the call',
  identity: 'Identify the caller',
  collect: 'Ask for details',
  schedule: 'Appointments',
  inform: 'Tell them something',
  handoff: 'Hand off',
  decide: 'Decide which way to go',
  close: 'Close the call',
};

interface AddStepDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  components: readonly AgentComponent[];
  shared: readonly WorkflowStep[];
  target: { channel: ComponentChannel; direction: ComponentCallDirection };
  /** Where the step will go, in words: "after Greeting". */
  position: string;
  /** A Decide step is added with the ready-made question it asks. */
  onAdd: (component: AgentComponent, preset?: string) => void;
  onAddShared: (step: WorkflowStep) => void;
  presets: readonly DecisionPreset[];
}

export function AddStepDialog({
  open,
  onOpenChange,
  components,
  shared,
  target,
  position,
  onAdd,
  onAddShared,
  presets,
}: AddStepDialogProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const matches = (text: string): boolean => !q || text.toLowerCase().includes(q);

  const groups = CATEGORY_ORDER.map((category) => ({
    category,
    items: components.filter(
      (c) => c.meta.category === category && matches(`${c.meta.title} ${c.meta.summary}`)
    ),
  })).filter((g) => g.items.length > 0);
  const titles = new Map(components.map((c) => [c.key, c.meta.title]));
  const sharedShown = shared.filter((s) =>
    matches(`${s.name} ${titles.get(s.componentKey) ?? ''}`)
  );

  const close = (): void => {
    setQuery('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="flex max-h-screen max-w-5xl flex-col sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Add a step</DialogTitle>
          <DialogDescription>It goes {position}.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            autoFocus
            placeholder="Search steps..."
            className="pl-9"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto pr-1">
          {groups.map(({ category, items }) => (
            <section key={category} className="space-y-2">
              <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {CATEGORY_TITLES[category]}
              </h4>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {items.flatMap((c) => {
                  const reason = unavailableReason(c, target);
                  // A decision is offered as the questions it can ask, so a
                  // practice picks "Do we know who referred them?" rather than
                  // an empty step it then has to understand.
                  if (c.key === 'Decide') {
                    return presets
                      .filter((p) => p.directions.includes(target.direction))
                      .filter((p) => matches(`${p.config.question} ${p.hint}`))
                      .map((p) => (
                        <Card
                          key={`${c.key}:${p.key}`}
                          title={p.config.question}
                          body={p.hint}
                          reason={reason}
                          onClick={() => {
                            onAdd(c, p.key);
                            close();
                          }}
                        />
                      ));
                  }
                  return (
                    <Card
                      key={c.key}
                      title={c.meta.title}
                      body={c.meta.summary}
                      reason={reason}
                      onClick={() => {
                        onAdd(c);
                        close();
                      }}
                    />
                  );
                })}
              </div>
            </section>
          ))}

          {sharedShown.length > 0 && (
            <section className="space-y-2">
              <h4 className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <Share2 className="h-3 w-3" />
                Shared steps
              </h4>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {sharedShown.map((s) => {
                  const component = components.find((c) => c.key === s.componentKey);
                  return (
                    <Card
                      key={s.id}
                      title={s.name}
                      body={`${titles.get(s.componentKey) ?? s.componentKey}, with its words and settings`}
                      reason={
                        component ? unavailableReason(component, target) : 'No longer offered'
                      }
                      onClick={() => {
                        onAddShared(s);
                        close();
                      }}
                    />
                  );
                })}
              </div>
            </section>
          )}

          {groups.length === 0 && sharedShown.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">No step matches.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Card({
  title,
  body,
  reason,
  onClick,
}: {
  title: string;
  body: string;
  reason: string | null;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      disabled={reason !== null}
      onClick={onClick}
      className={cn(
        'flex h-full flex-col items-start gap-1 rounded-md border p-3 text-left',
        'hover:border-primary hover:bg-accent',
        'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border disabled:hover:bg-transparent'
      )}
    >
      <span className="text-sm font-medium">{title}</span>
      <span className="line-clamp-3 text-xs text-muted-foreground">{body}</span>
      {reason && <span className="mt-auto pt-1 text-xs text-muted-foreground">{reason}</span>}
    </button>
  );
}
